import queryString from 'query-string';
import http from '../httpConfig';
import { API_ENDPOINTS } from '../utils/apiConstant';
import { buildPagingResponse } from '../utils/methods';
import { AddEditApi, List } from '../interfaces/transaction.interface';

const list = (payload: List) => {
  const { status, propertyId, transactionType, startDate, assignedTo, reviewedBy, endDate, debitCredit, invoiceId, searchText, vendorId, chargeable, paidBy, ...others } = payload;
  const paging = buildPagingResponse(others);
  const url = `${API_ENDPOINTS.listTransactions}?${queryString.stringify(paging)}`;
  return http.post(url, {
    search: searchText,
    property_id: propertyId,
    start_date: startDate,
    end_date: endDate,
    assigned_to: assignedTo,
    reconciled_by: reviewedBy,
    transaction_type_id: transactionType,
    transaction_status: status && status.length ? status : undefined,
    debit_credit: debitCredit,
    invoice_id: invoiceId,
    vendor_id: vendorId,
    chargeable: chargeable || undefined,
    paid_by: paidBy || undefined,
  });
};

const add = (payload: AddEditApi) => {
  return http.post(API_ENDPOINTS.transactions, payload);
};

const edit = (id: string, payload: AddEditApi) => {
  return http.put(`${API_ENDPOINTS.transactions}/${id}`, payload);
};

const get = (id: string) => {
  return http.get(`${API_ENDPOINTS.transactions}/${id}`);
};

const transactionTypeslookup = (payload: { searchText: string; orderBy: string; order: string; }) => {
  const url = `${API_ENDPOINTS.lookupTransactionTypes}?${queryString.stringify({ search: payload.searchText, from: 'transaction', order_by: payload.orderBy, order: payload.order })}`;
  return http.get(url);
};

const deleteTransaction = (id: string) => {
  return http.delete(`${API_ENDPOINTS.transactions}/${id}`);
};

const initAttachmentUpload = (transactionId: string, payload: { filename: string; sizeBytes: number; mimeType: string; totalChunks: number; }) => {
  return http.post(API_ENDPOINTS.transactionAttachmentInit(transactionId), payload);
};

const uploadAttachmentChunk = (transactionId: string, sessionId: string, chunkIndex: number, chunk: Blob) => {
  const formData = new FormData();
  formData.append('chunkIndex', String(chunkIndex));
  // `chunk` is a Blob (from File.slice()), not a File — pass an explicit
  // filename so the multipart part always has one (a bare Blob defaults to
  // "blob" per spec, but being explicit avoids relying on that default).
  formData.append('chunk', chunk, `chunk-${chunkIndex}`);

  // httpConfig sets a default 'Content-type: application/json' header on
  // every request from this axios instance. Axios's default transformRequest
  // checks that header BEFORE checking whether the body is FormData — if it
  // still reads as JSON, axios silently JSON.stringifies the FormData
  // (a Blob serializes to `{}`), which is exactly why `chunk` never arrived
  // as a file server-side. Clearing it per-request lets FormData pass
  // through untouched so axios/the browser can set the correct multipart
  // boundary automatically.
  return http.post(
    API_ENDPOINTS.transactionAttachmentChunk(transactionId, sessionId),
    formData,
    { headers: { 'Content-Type': undefined } }
  );
};

const finalizeAttachmentUpload = (transactionId: string, sessionId: string) => {
  return http.post(API_ENDPOINTS.transactionAttachmentFinalize(transactionId, sessionId));
};

const removeAttachment = (transactionId: string, attachmentId: number) => {
  return http.delete(API_ENDPOINTS.transactionAttachment(transactionId, String(attachmentId)));
};

const getAttachmentDownloadUrl = (transactionId: string, attachmentId: number) => {
  return http.get(API_ENDPOINTS.transactionAttachmentDownloadUrl(transactionId, String(attachmentId)));
};

const TransactionService = {
  list,
  get,
  add,
  edit,
  transactionTypeslookup,
  deleteTransaction,
  initAttachmentUpload,
  uploadAttachmentChunk,
  finalizeAttachmentUpload,
  removeAttachment,
  getAttachmentDownloadUrl,
};

export default TransactionService;


